import type { SceneId } from "../types";

export type AssistantPromptKind = "intro" | "help";
export type AssistantTaskEventKind = "success" | "error" | "reset";

export interface AssistantTaskEvent {
  id: number;
  sceneId: SceneId;
  kind: AssistantTaskEventKind;
}

export interface AssistantPromptState {
  sceneId: SceneId;
  visiblePrompt: AssistantPromptKind | null;
  consecutiveErrors: number;
  helpShownForRound: boolean;
  introSuppressedForVisit: boolean;
}

export type AssistantPromptAction =
  | { type: "scene-change"; sceneId: SceneId }
  | { type: "intro-ready" }
  | { type: "task-event"; event: AssistantTaskEvent }
  | { type: "dismiss-prompt" }
  | { type: "auto-dismiss-prompt"; prompt: AssistantPromptKind }
  | { type: "open-assistant" };

export function createAssistantPromptState(sceneId: SceneId): AssistantPromptState {
  return {
    sceneId,
    visiblePrompt: null,
    consecutiveErrors: 0,
    helpShownForRound: false,
    introSuppressedForVisit: false
  };
}

export function reduceAssistantPromptState(
  state: AssistantPromptState,
  action: AssistantPromptAction
): AssistantPromptState {
  if (action.type === "scene-change") {
    return action.sceneId === state.sceneId ? state : createAssistantPromptState(action.sceneId);
  }

  if (action.type === "intro-ready") {
    return state.visiblePrompt || state.introSuppressedForVisit
      ? state
      : { ...state, visiblePrompt: "intro" };
  }

  if (action.type === "dismiss-prompt") {
    return state.visiblePrompt ? { ...state, visiblePrompt: null } : state;
  }

  if (action.type === "auto-dismiss-prompt") {
    return state.visiblePrompt === action.prompt
      ? { ...state, visiblePrompt: null, introSuppressedForVisit: true }
      : state;
  }

  if (action.type === "open-assistant") {
    return {
      ...state,
      visiblePrompt: null,
      helpShownForRound: state.helpShownForRound || state.consecutiveErrors > 0
    };
  }

  if (action.event.sceneId !== state.sceneId) return state;

  if (action.event.kind === "success" || action.event.kind === "reset") {
    return {
      ...state,
      visiblePrompt: state.visiblePrompt === "help" ? null : state.visiblePrompt,
      consecutiveErrors: 0,
      helpShownForRound: false
    };
  }

  const consecutiveErrors = state.consecutiveErrors + 1;
  if (consecutiveErrors < 2 || state.helpShownForRound) {
    return { ...state, consecutiveErrors };
  }

  return {
    ...state,
    visiblePrompt: "help",
    consecutiveErrors,
    helpShownForRound: true
  };
}
